import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { Field, Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import NewModal from '../../../Shared/NewModal';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { uploadProfilePicture } from '../../../../store/customers/actionCreators';
import { PhotoUpload } from '../../../Shared/PhotoUploadComponents/PhotoUpload';
import { ProfileAvatar } from '../../../Shared/PhotoUploadComponents/ProfileAvatar';
import { TextInputFormField } from '../../../Shared/TextInputFormField';
import SelectFormField from '../../../Shared/SelectFormField';
import MaskedInput from '../../../Shared/MaskedInput';
import { RootState } from '../../../../store';
import { editOwner } from '../../../../store/hubOwner/actionCreators';
import { US_STATES } from '../../../../shared/constants';
import {
  lightBlack,
  selectFieldStyles,
  textFieldStyles,
} from '../../../../mainTheme';

interface Patient {
  first_name: string;
  last_name: string;
  email: string;
  facility: string;
  location: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  date_of_birth: string;
  gender: string;
  wing: string;
  room_number: string;
}

interface EditPatientDetailsModalProps {
  open: boolean;
  onClose: () => void;
  patient: Patient;
  profilePicture?: string | null;
  patientId: number;
  isFacilityPatient: boolean;
  onSuccess?: (actions: { edited: boolean; uploaded: boolean }) => void;
}

const nameRegex = /^[A-Za-z\s'-]+$/;

// No field is required on edit (OI-1586): legacy records often have blanks and
// a partial update must stay saveable. Only formats are checked, and only when
// a value is present. Names are the one exception: the API rejects an empty
// name, so an existing name may not be cleared — but a legacy blank stays
// saveable while left blank.
const nameField = (initial: string, message: string) =>
  Yup.string()
    .test(
      'name-not-cleared',
      'Required',
      (value) => !!(value && value.trim()) || !initial.trim(),
    )
    .test(
      'name-format',
      message,
      (value) => !value || !value.trim() || nameRegex.test(value),
    );

// Format-check the zip only when it was actually edited: no backend write path
// enforces a 5-digit zip, so a legacy record may hold e.g. "55124-1234" and
// must stay saveable when some other field is the change.
const zipIfEdited = (initial: string) =>
  Yup.string().test(
    'zip-format',
    'Enter a valid 5-digit zip code',
    (value) => value === initial || !value || /^\d{5}$/.test(value),
  );

// The API rejects an empty date_of_birth and offers no way to clear one, so an
// existing date may not be emptied — a legacy blank stays saveable while blank.
const dobNotCleared = (initial: string | null) =>
  Yup.string()
    .nullable()
    .test(
      'dob-not-cleared',
      'Date of birth cannot be cleared',
      (value) => !!value || !initial,
    );

// Diff both sides trimmed: a stored "Eagan " is not a change from "Eagan".
const trimmed = (value: string | null | undefined) => (value ?? '').trim();

const stateOptions = US_STATES.map((s) => ({ label: s.name, value: s.id }));

const buildValidationSchema = (patient: Patient) =>
  Yup.object({
    first_name: nameField(
      patient.first_name,
      'First name cannot contain numbers or special characters',
    ),
    last_name: nameField(
      patient.last_name,
      'Last name cannot contain numbers or special characters',
    ),
    phone: Yup.string().nullable(),
    address1: Yup.string(),
    address2: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    zipcode: zipIfEdited(patient.zipcode),
    date_of_birth: dobNotCleared(patient.date_of_birth),
    gender: Yup.string().nullable(),
    wing: Yup.string(),
    room_number: Yup.string(),
  });

const genderOptions = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
];

const GENDER_IDS: Record<string, number> = {
  Male: 1,
  Female: 2,
  Other: 90, // mapping to Decline to say in DB
};

function formatDate(isoDate: string | null) {
  // used to show the date in YYYY-MM-DD format for UI
  if (!isoDate) return '';
  // DOB is a calendar date — take the date part verbatim. Local-time parsing
  // of a UTC-midnight ISO string shifts it back a day west of UTC, which
  // would make an untouched DOB look "changed" on save.
  const dateOnly = /^(\d{4}-\d{2}-\d{2})/.exec(isoDate);
  if (dateOnly) return dateOnly[1];
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function toMMDDYYYY(dateStr: string) {
  // Used to format date strings to payload format API
  if (!dateStr) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (isoDateOnly) {
    const [, yyyy, mm, dd] = isoDateOnly;
    return `${mm}/${dd}/${yyyy}`;
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

// This to avoid unnecessary API payload data pass
function getChangedFields<T extends Record<string, any>>(
  initial: T,
  current: T,
) {
  const changed: Partial<T> = {};
  Object.keys(current).forEach((key) => {
    if (current[key] !== initial[key]) {
      (changed as any)[key] = current[key];
    }
  });
  return changed;
}

const ReadOnlySelectField = ({
  label,
  name,
  value,
}: {
  label: string;
  name: string;
  value: string;
}) => (
  <Field
    label={label}
    name={name}
    placeholder={`Select ${label.toLowerCase()}`}
    component={SelectFormField}
    options={[{ value: value || 'N/A', label: value || 'N/A' }]}
    useNewStyles
    sx={{
      ...selectFieldStyles,
      '& .MuiSelect-select': { pointerEvents: 'none', color: lightBlack },
    }}
  />
);

const EditPatientDetailsModal: React.FC<EditPatientDetailsModalProps> = ({
  open,
  onClose,
  patient,
  profilePicture,
  patientId,
  isFacilityPatient,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [photoData, setPhotoData] = useState<{
    blob: Blob | null;
    previewUrl: string | null;
  }>({
    blob: null,
    previewUrl: profilePicture || null,
  });
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);

  const { isLoading: profileUploadLoading } = useSelector(
    (state: RootState) => state.customers,
  );
  const { isLoading: isUpdatingOwner } = useSelector(
    (state: RootState) => state.hubOwner,
  );

  const formikRef = useRef<any>(null);

  const validationSchema = useMemo(
    () => buildValidationSchema(patient),
    [patient.first_name, patient.last_name],
  );

  const handleFormSubmit = async (
    values: Patient,
    _formikHelpers: FormikHelpers<Patient>,
  ) => {
    // Trim both sides before diffing: '  John ' isn't a real change from
    // 'John', a stored 'Eagan ' isn't a change from 'Eagan', and a
    // whitespace-only entry must not go over the wire as a "value".
    const initialValues = {
      first_name: trimmed(patient.first_name),
      last_name: trimmed(patient.last_name),
      phone: trimmed(patient.phone),
      address1: trimmed(patient.address1),
      address2: trimmed(patient.address2),
      city: trimmed(patient.city),
      state: patient.state,
      zipcode: trimmed(patient.zipcode),
      gender_id: GENDER_IDS[patient.gender] || 0,
      date_of_birth: formatDate(patient.date_of_birth),
      ...(isFacilityPatient && {
        wing: trimmed(patient.wing),
        room_number: trimmed(patient.room_number),
      }),
    };

    const changedFields = getChangedFields(initialValues, {
      first_name: trimmed(values.first_name),
      last_name: trimmed(values.last_name),
      phone: trimmed(values.phone),
      address1: trimmed(values.address1),
      address2: trimmed(values.address2),
      city: trimmed(values.city),
      state: values.state,
      zipcode: trimmed(values.zipcode),
      gender_id: GENDER_IDS[values.gender] || 0,
      date_of_birth: formatDate(values.date_of_birth),
      // Wing / room only exist for facility patients; never send them otherwise.
      ...(isFacilityPatient && {
        wing: trimmed(values.wing),
        room_number: trimmed(values.room_number),
      }),
    });

    // Validation blocks clearing an existing date, so a changed dob here is
    // always a real value.
    if (changedFields.date_of_birth) {
      changedFields.date_of_birth = toMMDDYYYY(values.date_of_birth);
    }
    if (changedFields.phone === '') {
      // The API's phone format check runs on any non-null string, so a cleared
      // number must go over as null.
      (changedFields as { phone?: string | null }).phone = null;
    }

    let edited = false;
    let uploaded = false;

    try {
      // Update patient details
      if (Object.keys(changedFields).length > 0) {
        await dispatch(editOwner(patientId, changedFields));
        edited = true;
      }
      // Update profile picture
      if (photoData.blob && photoData.previewUrl !== profilePicture) {
        await dispatch(uploadProfilePicture(patientId, photoData.blob));
        uploaded = true;
      }
      onClose();
      // Only calls Get APIS based on changes
      onSuccess?.({ edited, uploaded });
    } catch (err: any) {
      // e.g. the server rejects date_of_birth changes for email-less
      // patients (403) with an actionable "contact customer care" message
      if (err?.status === 403 && typeof err?.body === 'string') {
        toast.error(err.body);
      } else {
        toast.error('Failed to update patient details');
      }
    }
  };

  useEffect(() => {
    if (!open) {
      setPhotoData({ blob: null, previewUrl: profilePicture || null });
    }
  }, [open, profilePicture]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        first_name: patient.first_name,
        last_name: patient.last_name,
        email: patient.email,
        facility: patient.facility || 'N/A',
        location: patient.location || 'N/A',
        phone: patient.phone,
        address1: patient.address1,
        address2: patient.address2,
        city: patient.city,
        state: patient.state,
        zipcode: patient.zipcode,
        date_of_birth: formatDate(patient.date_of_birth),
        gender: patient.gender === 'Non-binary' ? 'Other' : patient.gender,
        wing: patient.wing,
        room_number: patient.room_number,
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      validateOnChange={false}
      enableReinitialize
    >
      {(formik) => (
        <NewModal
          maxWidth="xs"
          onClose={onClose}
          title="Edit patient details"
          showCloseX
          {...(!showPhotoUpload && {
            actions: {
              secondaryAction: { text: 'Back', onClick: onClose },
              primaryAction: {
                text: 'Save',
                onClick: () => formikRef.current?.submitForm(),
                disabled:
                  isUpdatingOwner ||
                  profileUploadLoading ||
                  (!formik.dirty && photoData.previewUrl === profilePicture),
              },
            },
            isLoading: isUpdatingOwner || profileUploadLoading,
          })}
          isLoading={isUpdatingOwner || profileUploadLoading}
        >
          {!showPhotoUpload && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              mb="24px"
            >
              <ProfileAvatar
                photo={photoData.previewUrl}
                onAddPhoto={() => setShowPhotoUpload(true)}
              />
            </Box>
          )}
          {showPhotoUpload ? (
            <PhotoUpload
              onBack={() => {
                setShowPhotoUpload(false);
                setPhotoData({
                  blob: null,
                  previewUrl: profilePicture || null,
                });
              }}
              onSave={(blob, previewUrl) => {
                setPhotoData({ blob, previewUrl });
                setShowPhotoUpload(false);
              }}
              initialPhoto={photoData.previewUrl}
            />
          ) : (
            <Form>
              <Box display="flex" flexDirection="column" gap={2}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      label="First Name"
                      name="first_name"
                      type="text"
                      placeholder="First Name"
                      component={TextInputFormField}
                      sx={textFieldStyles}
                      // Without an email, name+dob is the patient's identity
                      // key — the server rejects name changes (403)
                      disabled={!patient.email}
                      {...(!patient.email && {
                        helperText: 'Contact customer care to change the name.',
                      })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      label="Last Name"
                      name="last_name"
                      type="text"
                      placeholder="Last Name"
                      component={TextInputFormField}
                      sx={textFieldStyles}
                      disabled={!patient.email}
                      {...(!patient.email && {
                        helperText: 'Contact customer care to change the name.',
                      })}
                    />
                  </Grid>
                </Grid>
                {isFacilityPatient && (
                  <>
                    <ReadOnlySelectField
                      label="Facility name"
                      name="facility"
                      value={patient.facility}
                    />
                    <ReadOnlySelectField
                      label="Facility location"
                      name="location"
                      value={patient.location}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Field
                          label="Wing"
                          name="wing"
                          type="text"
                          placeholder="Wing"
                          component={TextInputFormField}
                          sx={textFieldStyles}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          label="Room number"
                          name="room_number"
                          type="text"
                          placeholder="Room number"
                          component={TextInputFormField}
                          sx={textFieldStyles}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
                <Field
                  label="Email"
                  name="email"
                  type="text"
                  placeholder="Email"
                  component={TextInputFormField}
                  sx={textFieldStyles}
                  InputProps={{ readOnly: true }}
                />
                <Field
                  label="Address 1"
                  name="address1"
                  type="text"
                  placeholder="Address 1"
                  component={TextInputFormField}
                  sx={textFieldStyles}
                />
                <Field
                  label="Address 2"
                  name="address2"
                  type="text"
                  placeholder="Address 2"
                  component={TextInputFormField}
                  sx={textFieldStyles}
                />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      label="City"
                      name="city"
                      type="text"
                      placeholder="City"
                      component={TextInputFormField}
                      sx={textFieldStyles}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      label="State"
                      name="state"
                      placeholder="State"
                      component={SelectFormField}
                      options={stateOptions}
                      sx={selectFieldStyles}
                      useNewStyles
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      label="Zip code"
                      name="zipcode"
                      type="text"
                      placeholder="Zip code"
                      component={TextInputFormField}
                      sx={textFieldStyles}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      label="Phone number"
                      name="phone"
                      format="(###) ###-####"
                      placeholder="Phone number"
                      component={MaskedInput}
                      sx={textFieldStyles}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      label="Date of Birth"
                      name="date_of_birth"
                      type="date"
                      placeholder="MM/DD/YYYY"
                      component={TextInputFormField}
                      sx={textFieldStyles}
                      InputLabelProps={{ shrink: true }}
                      // Without an email, name+dob is the patient's identity
                      // key — the server rejects dob changes (403)
                      disabled={!patient.email}
                      {...(!patient.email && {
                        helperText:
                          'Contact customer care to change the date of birth.',
                      })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      label="Gender"
                      name="gender"
                      placeholder="Gender"
                      component={SelectFormField}
                      options={genderOptions}
                      sx={selectFieldStyles}
                      useNewStyles
                    />
                  </Grid>
                </Grid>
              </Box>
            </Form>
          )}
        </NewModal>
      )}
    </Formik>
  );
};

export default EditPatientDetailsModal;
